import type {
  ObservabilityBulkFailureTransitionRequest,
  ObservabilityFailureTransitionAction,
  ObservabilityUserAccessItem,
} from '@scatterin/api-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { analyticsApi } from '@/lib/apiClient';
import type { ObservabilityLogLookupRequest } from '@/lib/apiClient';
import { buildEndpointScope, buildLogLookupWindow, buildObservabilityFilter } from '@/lib/observabilityFilters';
import type { AnalyticsUrlState } from '@/lib/urlState';

const staleTimeMs = 30_000;

export const useSummaryQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);
  const request = { filter, topLimit: state.topLimit };

  return useQuery({
    queryFn: () => analyticsApi.summary(request),
    queryKey: ['observability', 'summary', request],
    staleTime: staleTimeMs,
  });
};

export const usePreviousSummaryQuery = (state: AnalyticsUrlState) => {
  const windowDuration = state.end.getTime() - state.start.getTime();
  const previousState = {
    ...state,
    end: new Date(state.end.getTime() - windowDuration),
    start: new Date(state.start.getTime() - windowDuration),
  };
  const filter = buildObservabilityFilter(previousState);
  const scope = buildEndpointScope(previousState);

  return useQuery({
    queryFn: () => analyticsApi.summary({ filter, topLimit: state.topLimit }),
    queryKey: ['observability', 'summary', 'previous-window', scope],
    staleTime: staleTimeMs,
  });
};

export const useCatalogQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);
  const scope = buildEndpointScope(state);

  return useQuery({
    queryFn: () => analyticsApi.catalog({ filter, limit: state.topLimit }),
    queryKey: ['observability', 'catalog', scope],
    staleTime: staleTimeMs,
  });
};

export const useCompareQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);
  const scope = buildEndpointScope(state);

  return useQuery({
    queryFn: () =>
      analyticsApi.compare({
        dimension: state.compareDimension,
        filter,
        limit: state.topLimit,
        metric: state.compareMetric,
      }),
    queryKey: ['observability', 'compare', scope],
    staleTime: staleTimeMs,
  });
};

export const usePerformanceCompareQuery = (
  state: AnalyticsUrlState,
  metric: AnalyticsUrlState['compareMetric'],
  dimension: AnalyticsUrlState['compareDimension'],
  enabled = true,
) => {
  const filter = buildObservabilityFilter(state);
  const scope = buildEndpointScope(state);

  return useQuery({
    enabled,
    queryFn: () => analyticsApi.compare({ dimension, filter, limit: state.topLimit, metric }),
    queryKey: ['observability', 'performance-compare', scope, metric, dimension],
    staleTime: staleTimeMs,
  });
};

export const useUsageInventoryQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);
  const scope = buildEndpointScope(state);

  return useQuery({
    queryFn: () => analyticsApi.usageInventory({ filter, limit: state.queueLimit }),
    queryKey: ['observability', 'usage-inventory', scope],
    staleTime: staleTimeMs,
  });
};

export const useUsageSubjectQuery = (
  state: AnalyticsUrlState,
  subjectType: 'project' | 'user' | undefined,
  subjectId: string | undefined,
) => {
  const filter = buildObservabilityFilter(state);

  return useQuery({
    enabled: Boolean(subjectType && subjectId),
    queryFn: () =>
      analyticsApi.usageSubject({
        filter,
        recentRunLimit: 50,
        subjectId: subjectId ?? '',
        subjectType: subjectType ?? 'user',
      }),
    queryKey: ['observability', 'usage-subject', filter, subjectType, subjectId],
    staleTime: staleTimeMs,
  });
};

export const useServiceInventoryQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);
  const request = {
    filter,
    limit: state.resourcePageSize,
    offset: state.resourceOffset,
    resourceTypes: state.resourceTypes,
    search: state.search || undefined,
  };

  return useQuery({
    queryFn: () => analyticsApi.serviceInventory(request),
    queryKey: ['observability', 'service-inventory', request],
    staleTime: staleTimeMs,
  });
};

export const useServiceDetailQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);

  return useQuery({
    enabled: Boolean(state.resourceKey && state.resourceType),
    queryFn: () =>
      analyticsApi.serviceDetail({
        factLimit: 50,
        filter,
        resourceKey: state.resourceKey ?? '',
        resourceType: state.resourceType ?? 'lambda_function',
      }),
    queryKey: ['observability', 'service-detail', filter, state.resourceType, state.resourceKey],
    staleTime: staleTimeMs,
  });
};

export const useUsageUserIdentitiesQuery = (state: AnalyticsUrlState, ownerUserIds: string[], enabled = true) => {
  const userIds = [...new Set(ownerUserIds)].toSorted((left, right) => left.localeCompare(right));

  return useQuery({
    enabled: enabled && userIds.length > 0,
    queryFn: async () => {
      const identities: Record<string, ObservabilityUserAccessItem> = {};
      const unresolved = new Set(userIds);
      const visitedTokens = new Set<string>();
      let paginationToken: string | undefined;

      do {
        const response = await analyticsApi.userAccessList({
          environment: state.env,
          limit: 60,
          paginationToken,
        });
        for (const user of response.users ?? []) {
          if (user.sub && unresolved.has(user.sub)) {
            identities[user.sub] = user;
            unresolved.delete(user.sub);
          }
        }

        const nextToken = response.paginationToken ?? undefined;
        if (!nextToken || unresolved.size === 0 || visitedTokens.has(nextToken)) break;
        visitedTokens.add(nextToken);
        paginationToken = nextToken;
      } while (paginationToken);

      return identities;
    },
    queryKey: ['observability', 'usage-user-identities', state.env, userIds],
    staleTime: staleTimeMs,
  });
};

export const useEnvironmentHealthQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);
  const scope = buildEndpointScope(state);

  return useQuery({
    queryFn: () => analyticsApi.environmentHealth({ filter, topLimit: state.topLimit }),
    queryKey: ['observability', 'environment-health', scope],
    staleTime: staleTimeMs,
  });
};

export const useUserAccessQuery = (state: AnalyticsUrlState) => {
  const scope = buildEndpointScope(state);

  return useQuery({
    queryFn: () =>
      analyticsApi.userAccessList({
        environment: state.env,
        limit: 60,
        paginationToken: state.userPageToken ?? undefined,
      }),
    queryKey: ['observability', 'user-access', scope],
    staleTime: staleTimeMs,
  });
};

export const useFailureGroupsQuery = (state: AnalyticsUrlState) => {
  const request = {
    filter: buildObservabilityFilter(state),
    limit: state.queueLimit,
    offset: state.queueOffset,
    status: state.queueView,
  };

  return useQuery({
    queryFn: () => analyticsApi.failureGroups(request),
    queryKey: ['observability', 'failure-groups', request],
    staleTime: staleTimeMs,
  });
};

export const useFailureDetailQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);

  return useQuery({
    enabled: Boolean(state.failureId),
    queryFn: () => analyticsApi.failureQueue({ failureId: state.failureId, filter, limit: 1, offset: 0, view: 'all' }),
    queryKey: ['observability', 'failure-detail', filter, state.failureId],
    staleTime: staleTimeMs,
  });
};

export const useFailureQueueQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);
  const scope = buildEndpointScope(state);

  return useQuery({
    queryFn: () => analyticsApi.failureQueue({ filter, limit: state.queueLimit, offset: state.queueOffset, view: state.queueView }),
    queryKey: ['observability', 'failure-queue', scope],
    staleTime: staleTimeMs,
  });
};

export const useRunDetailQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);
  const scope = buildEndpointScope(state);

  return useQuery({
    enabled: Boolean(state.runId),
    queryFn: () => analyticsApi.runDetail({ filter, rawFactLimit: state.rawFactLimit, runId: state.runId ?? undefined }),
    queryKey: ['observability', 'run-detail', scope],
    staleTime: staleTimeMs,
  });
};

export const useRunSearchQuery = (state: AnalyticsUrlState, enabled = true) => {
  const filter = buildObservabilityFilter(state);
  const request = {
    filter,
    limit: state.runPageSize,
    offset: state.runOffset,
    search: state.search || undefined,
    statuses: state.runStatuses,
    workerImageVersions: state.workerImageVersions,
    workloadSizeClasses: state.workloadSizeClasses,
  };

  return useQuery({
    enabled,
    queryFn: () => analyticsApi.runSearch(request),
    queryKey: ['observability', 'run-search', request],
    staleTime: staleTimeMs,
  });
};

export const useSessionDetailQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);
  const scope = buildEndpointScope(state);

  return useQuery({
    enabled: Boolean(state.sessionId),
    queryFn: () => analyticsApi.sessionDetail({ filter, rawFactLimit: state.rawFactLimit, sessionId: state.sessionId ?? undefined }),
    queryKey: ['observability', 'session-detail', scope],
    staleTime: staleTimeMs,
  });
};

export const useSessionSearchQuery = (state: AnalyticsUrlState) => {
  const filter = buildObservabilityFilter(state);
  const scope = buildEndpointScope(state);

  return useQuery({
    queryFn: () =>
      analyticsApi.sessionSearch({
        browsers: state.sessionBrowsers,
        devices: state.sessionDevices,
        filter,
        hasClientErrors: state.sessionErrorStatus === 'all' ? undefined : state.sessionErrorStatus === 'with',
        limit: state.sessionPageSize,
        offset: state.sessionOffset,
        routes: state.sessionRoutes,
        search: state.search || undefined,
        vitalNames: state.sessionVitalNames,
      }),
    queryKey: ['observability', 'session-search', scope],
    staleTime: staleTimeMs,
  });
};

const buildLogRequest = (state: AnalyticsUrlState): ObservabilityLogLookupRequest => {
  const window = buildLogLookupWindow(state);
  return {
    endTime: window.endTime,
    environment: state.env,
    failureId: state.failureId ?? undefined,
    logGroup: state.logGroup ?? undefined,
    logStream: state.logStream ?? undefined,
    nextToken: state.logNextToken ?? undefined,
    maxEvents: state.queueLimit,
    runId: state.runId ?? undefined,
    startTime: window.startTime,
  };
};

export const useLogsQuery = (state: AnalyticsUrlState) => {
  const scope = { ...buildEndpointScope(state), logWindow: buildLogLookupWindow(state) };
  const hasReference = Boolean(state.failureId || state.runId || state.logGroup);

  return useQuery({
    enabled: hasReference,
    queryFn: () => analyticsApi.logs(buildLogRequest(state)),
    queryKey: ['observability', 'logs', scope],
    staleTime: staleTimeMs,
  });
};

export const useFailureTransitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: {
      action: ObservabilityFailureTransitionAction;
      environment: AnalyticsUrlState['env'];
      failureId: string;
      reason?: string;
    }) =>
      analyticsApi.failureTransition({
        action: request.action,
        environment: request.environment,
        failureId: request.failureId,
        reason: request.reason,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['observability'] });
    },
  });
};

export const useBulkFailureTransitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: ObservabilityBulkFailureTransitionRequest) => {
      if (request.groupKeys) {
        return analyticsApi.bulkTransitionFailures(request);
      }
      const items = [];
      const errors = [];
      const failureIds = request.failureIds ?? [];

      for (let offset = 0; offset < failureIds.length; offset += 50) {
        const response = await analyticsApi.bulkTransitionFailures({
          action: request.action,
          environment: request.environment,
          failureIds: failureIds.slice(offset, offset + 50),
          reason: request.reason,
        });
        items.push(...(response.items ?? []));
        errors.push(...(response.errors ?? []));
      }

      return { errors, items, updatedCount: items.length };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['observability'] });
    },
  });
};

export const useUserAccessUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: { environment: AnalyticsUrlState['env']; saasAccess: boolean; username: string }) =>
      analyticsApi.userAccessUpdate(request),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['observability', 'user-access'] });
    },
  });
};
